import React, { useEffect } from 'react';
import { Modal, Form, Button, Card, Row, Col, Badge, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPlus, faPills, faBoxes, faEdit, faTimes, faCheck, faInfoCircle, faExternalLinkAlt
} from '@fortawesome/free-solid-svg-icons';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { ProdutoParaInventarioType, LoteInventarioType, IncluirProdutoInventarioType } from '../../../../data/sngpc';
import { useToast } from '../../../../providers/ToastProvider';
import { useIncluirProdutoInventario } from '../../../../hooks/useSngpc';
import { useBreakpoints } from '../../../../providers/BreakpointsProvider';
import LinkProduto from '../../../../components/common/LinkProduto';

interface ModalIncluirProdutoProps {
    show: boolean;
    onHide: () => void;
    produto: ProdutoParaInventarioType | null;
}

interface FormData {
    lotes: LoteInventarioType[];
    informacoesCadastrais: {
        lista: string;
        classeTerapeutica: string;
        receituario: string;
        unidade: string;
    };
}

const ModalIncluirProduto: React.FC<ModalIncluirProdutoProps> = ({ show, onHide, produto }) => {
    const { showToast } = useToast();
    const incluirProdutoMutation = useIncluirProdutoInventario();
    const { breakpoints } = useBreakpoints();
    const isMobile = breakpoints.down('md');

    const { control, handleSubmit, reset, formState: { errors } } = useForm<FormData>({
        defaultValues: {
            lotes: [{
                lote: '',
                quantidade: 0,
                rms: ''
            }],
            informacoesCadastrais: {
                lista: '',
                classeTerapeutica: '',
                receituario: '',
                unidade: '0' // Padrão para Caixa
            }
        }
    });

    const { fields, append, remove } = useFieldArray({
        control,
        name: "lotes"
    });

    // Resetar formulário quando o modal abrir com um novo produto
    useEffect(() => {
        if (produto && show) {
            reset({
                lotes: [{
                    lote: '',
                    quantidade: 0,
                    rms: produto.rms || ''
                }],
                informacoesCadastrais: {
                    lista: produto.lista?.toString() || '',
                    classeTerapeutica: produto.classeTerapeutica?.toString() || '',
                    receituario: produto.receituario?.toString() || '',
                    unidade: '0' // Padrão para Caixa
                }
            });
        }
    }, [produto, show, reset]);

    const adicionarLote = () => {
        append({
            lote: '',
            quantidade: 0,
            rms: produto?.rms || ''
        });
    };

    const onSubmit = async (data: FormData) => {
        if (!produto) return;

        // Validar se todos os lotes têm dados obrigatórios
        const lotesValidos = data.lotes.filter(lote =>
            lote.lote.trim() !== '' && lote.quantidade > 0 && lote.rms.trim() !== ''
        );

        if (lotesValidos.length === 0) {
            showToast('Preencha pelo menos um lote com todas as informações obrigatórias.', 'error');
            return;
        }

        // Validar campos obrigatórios para o inventário
        if (!data.informacoesCadastrais.classeTerapeutica) {
            showToast('Classe Terapêutica é obrigatória.', 'error');
            return;
        }

        if (!data.informacoesCadastrais.unidade) {
            showToast('Unidade é obrigatória.', 'error');
            return;
        }

        const dadosInclusao: IncluirProdutoInventarioType = {
            produtoId: produto.id,
            lotes: lotesValidos,
            lista: data.informacoesCadastrais.lista ? parseInt(data.informacoesCadastrais.lista) : undefined,
            classeTerapeutica: data.informacoesCadastrais.classeTerapeutica ? parseInt(data.informacoesCadastrais.classeTerapeutica) : undefined,
            receituario: data.informacoesCadastrais.receituario ? parseInt(data.informacoesCadastrais.receituario) : undefined,
            unidade: data.informacoesCadastrais.unidade ? parseInt(data.informacoesCadastrais.unidade) : 0
        };

        try {
            await incluirProdutoMutation.mutateAsync(dadosInclusao);
            onHide(); // Fechar modal após sucesso
        } catch (error) {
            // Erro já tratado no hook
            console.error('Erro ao incluir produto:', error);
        }
    };

    if (!produto) return null;

    return (
        <Modal show={show} onHide={onHide} size={isMobile ? undefined : "lg"} centered fullscreen={isMobile ? "sm-down" : undefined}>
            <Modal.Header closeButton>
                <Modal.Title className={isMobile ? 'h6' : ''}>
                    <FontAwesomeIcon icon={faPlus} className="me-2 text-primary-emphasis" />
                    {produto?.jaNoInventario ? (isMobile ? 'Novo Lote' : 'Adicionar Novo Lote') : (isMobile ? 'Incluir Produto' : 'Incluir Produto no Inventário')}
                </Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit(onSubmit)}>
                <Modal.Body>
                    {/* Alerta para produto já existente */}
                    {produto?.jaNoInventario && (
                        <Alert variant="info" className="border border-info border-opacity-25 bg-info-subtle mb-4">
                            <div className="text-info-emphasis">
                                <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                                <strong>Produto já no inventário:</strong> Este produto já possui lotes no inventário.
                                Você pode adicionar novos lotes com diferentes números/RMS.
                            </div>
                        </Alert>
                    )}
                    {/* Informações do Produto */}
                    <Card className="mb-4">
                        <Card.Header>
                            <h6 className="mb-0">
                                <FontAwesomeIcon icon={faPills} className="me-2" />
                                Informações do Produto
                            </h6>
                        </Card.Header>
                        <Card.Body>
                            <Row>
                                <Col md={8}>
                                    <LinkProduto produtoId={produto.id}>
                                        <strong>{produto.nome}</strong>
                                    </LinkProduto>
                                    <div className="text-body-secondary small">
                                        Código: {produto.codigo}
                                        {produto.codigoBarras && ` | EAN: ${produto.codigoBarras}`}
                                    </div>
                                    <div className="text-body-secondary small">
                                        {produto.fabricante || 'Sem fabricante'}
                                    </div>
                                </Col>
                                <Col md={4} className="text-end">
                                    <div className={produto.estoqueAtual > 0 ? 'text-success-emphasis' : 'text-body-secondary'}>
                                        Estoque: {produto.estoqueAtual} {produto.unidade || 'UN'}
                                    </div>
                                    {produto.ehControlado && (
                                        <Badge bg="warning">Controlado</Badge>
                                    )}
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Lotes */}
                    <Card className="mb-4">
                        <Card.Header className="d-flex justify-content-between align-items-center">
                            <h6 className="mb-0">
                                <FontAwesomeIcon icon={faBoxes} className="me-2" />
                                Lotes do Produto
                            </h6>
                            <Button type="button" variant="outline-primary" size="sm" onClick={adicionarLote}>
                                <FontAwesomeIcon icon={faPlus} className="me-1" />
                                Adicionar Lote
                            </Button>
                        </Card.Header>
                        <Card.Body>
                            {fields.map((field, index) => (
                                <div key={field.id} className="border rounded p-3 mb-3">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <h6 className="mb-0">Lote {index + 1}</h6>
                                        {fields.length > 1 && (
                                            <Button
                                                type="button"
                                                variant="outline-danger"
                                                size="sm"
                                                onClick={() => remove(index)}
                                            >
                                                <FontAwesomeIcon icon={faTimes} />
                                            </Button>
                                        )}
                                    </div>
                                    <Row>
                                        <Col md={4}>
                                            <Form.Group>
                                                <Form.Label>Lote *</Form.Label>
                                                <Controller
                                                    name={`lotes.${index}.lote`}
                                                    control={control}
                                                    rules={{ required: 'Lote é obrigatório' }}
                                                    render={({ field }) => (
                                                        <Form.Control
                                                            {...field}
                                                            type="text"
                                                            placeholder="Número do lote"
                                                            isInvalid={!!errors.lotes?.[index]?.lote}
                                                        />
                                                    )}
                                                />
                                                {errors.lotes?.[index]?.lote && (
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors.lotes[index]?.lote?.message}
                                                    </Form.Control.Feedback>
                                                )}
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group>
                                                <Form.Label>Quantidade *</Form.Label>
                                                <Controller
                                                    name={`lotes.${index}.quantidade`}
                                                    control={control}
                                                    rules={{
                                                        required: 'Quantidade é obrigatória',
                                                        min: { value: 0.01, message: 'Quantidade deve ser maior que 0' }
                                                    }}
                                                    render={({ field }) => (
                                                        <Form.Control
                                                            {...field}
                                                            type="number"
                                                            min="0"
                                                            step="0.01"
                                                            placeholder="0"
                                                            isInvalid={!!errors.lotes?.[index]?.quantidade}
                                                            onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                                                        />
                                                    )}
                                                />
                                                {errors.lotes?.[index]?.quantidade && (
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors.lotes[index]?.quantidade?.message}
                                                    </Form.Control.Feedback>
                                                )}
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group>
                                                <Form.Label>RMS *</Form.Label>
                                                <Controller
                                                    name={`lotes.${index}.rms`}
                                                    control={control}
                                                    rules={{ required: 'RMS é obrigatório' }}
                                                    render={({ field }) => (
                                                        <Form.Control
                                                            {...field}
                                                            type="text"
                                                            placeholder="Registro no Ministério da Saúde"
                                                            isInvalid={!!errors.lotes?.[index]?.rms}
                                                        />
                                                    )}
                                                />
                                                {errors.lotes?.[index]?.rms && (
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors.lotes[index]?.rms?.message}
                                                    </Form.Control.Feedback>
                                                )}
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </div>
                            ))}
                        </Card.Body>
                    </Card>

                    {/* Informações Cadastrais */}
                    <Card>
                        <Card.Header>
                            <h6 className="mb-0">
                                <FontAwesomeIcon icon={faEdit} className="me-2" />
                                Revisar Informações Cadastrais
                            </h6>
                        </Card.Header>
                        <Card.Body>
                            <Row>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Lista</Form.Label>
                                        <Controller
                                            name="informacoesCadastrais.lista"
                                            control={control}
                                            render={({ field }) => (
                                                <Form.Select {...field}>
                                                    <option value="">Selecione...</option>
                                                    <option value="0">A1</option>
                                                    <option value="1">A2</option>
                                                    <option value="2">A3</option>
                                                    <option value="3">A4</option>
                                                    <option value="4">B1</option>
                                                    <option value="5">B2</option>
                                                    <option value="6">C1</option>
                                                    <option value="7">C2</option>
                                                    <option value="8">C3</option>
                                                    <option value="9">C4</option>
                                                    <option value="10">C5</option>
                                                    <option value="11">D1</option>
                                                    <option value="12">D2</option>
                                                    <option value="13">F1</option>
                                                    <option value="14">F2</option>
                                                    <option value="15">F3</option>
                                                    <option value="16">F4</option>
                                                </Form.Select>
                                            )}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Classe Terapêutica *</Form.Label>
                                        <Controller
                                            name="informacoesCadastrais.classeTerapeutica"
                                            control={control}
                                            rules={{ required: 'Classe Terapêutica é obrigatória' }}
                                            render={({ field, fieldState: { error } }) => (
                                                <Form.Select {...field} isInvalid={!!error}>
                                                    <option value="">Selecione...</option>
                                                    <option value="0">Antimicrobiano</option>
                                                    <option value="1">Sujeito a controle especial</option>
                                                </Form.Select>
                                            )}
                                        />
                                        {errors.informacoesCadastrais?.classeTerapeutica && (
                                            <Form.Control.Feedback type="invalid">
                                                {errors.informacoesCadastrais.classeTerapeutica.message}
                                            </Form.Control.Feedback>
                                        )}
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Receituário</Form.Label>
                                        <Controller
                                            name="informacoesCadastrais.receituario"
                                            control={control}
                                            render={({ field }) => (
                                                <Form.Select {...field}>
                                                    <option value="">Selecione...</option>
                                                    <option value="0">Receita branca</option>
                                                    <option value="1">Notificação azul</option>
                                                    <option value="2">Notificação Branca</option>
                                                    <option value="3">Notificação Amarela</option>
                                                    <option value="4">Receita Antimicrobiano em 2 vias</option>
                                                </Form.Select>
                                            )}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Unidade *</Form.Label>
                                        <Controller
                                            name="informacoesCadastrais.unidade"
                                            control={control}
                                            rules={{ required: 'Unidade é obrigatória' }}
                                            render={({ field, fieldState: { error } }) => (
                                                <Form.Select {...field} isInvalid={!!error}>
                                                    <option value="0">Caixa</option>
                                                    <option value="1">Frasco</option>
                                                </Form.Select>
                                            )}
                                        />
                                        {errors.informacoesCadastrais?.unidade && (
                                            <Form.Control.Feedback type="invalid">
                                                {errors.informacoesCadastrais.unidade.message}
                                            </Form.Control.Feedback>
                                        )}
                                    </Form.Group>
                                </Col>
                            </Row>
                            <div className="mt-3">
                                <Alert variant="info" className="border border-info border-opacity-25 bg-info-subtle mb-0">
                                    <div className="text-info-emphasis">
                                        <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                                        <strong>Informação:</strong> Classe Terapêutica e Unidade são obrigatórios. Lista e Receituário são opcionais. Todos os campos preenchidos (Lista, Classe Terapêutica e Receituário) atualizarão o cadastro do produto.
                                    </div>
                                </Alert>
                            </div>
                        </Card.Body>
                    </Card>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide}>
                        Cancelar
                    </Button>
                    <Button
                        type="submit"
                        variant="primary"
                        disabled={incluirProdutoMutation.isPending}
                    >
                        <FontAwesomeIcon icon={faCheck} className="me-2" />
                        {incluirProdutoMutation.isPending ? 'Incluindo...' : 'Incluir no Inventário'}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default ModalIncluirProduto; 